import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import EditIcon from '@mui/icons-material/Edit';

import { UploadSummary } from './GDOBulkUploadTypes';

interface GDOStatusSummaryProps {
  summary: UploadSummary;
  onDownloadReport: () => void;
  onDownloadInvalidTemplate: () => void;
  onGoToGDOList: () => void;
  onUploadAnother: () => void;
}

export function GDOStatusSummary({
  summary,
  onDownloadReport,
  onDownloadInvalidTemplate,
  onGoToGDOList,
  onUploadAnother,
}: GDOStatusSummaryProps) {
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F3764', mb: 3 }}>
        Upload Complete
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              backgroundColor: 'rgba(245, 248, 252, 1) !important',
              border: '2px solid #4CAF50',
              borderRadius: 2,
              height: '100%',
              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.15)',
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
              <Typography variant="h4" sx={{ color: '#4CAF50', fontWeight: 700 }}>
                {summary.successCount}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                Successfully Uploaded
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            sx={{
              backgroundColor: 'rgba(245, 248, 252, 1) !important',
              border: '2px solid #F44336',
              borderRadius: 2,
              height: '100%',
              boxShadow: '0 2px 8px rgba(244, 67, 54, 0.15)',
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <ErrorIcon sx={{ fontSize: 48, color: '#F44336', mb: 1 }} />
              <Typography variant="h4" sx={{ color: '#F44336', fontWeight: 700 }}>
                {summary.errorCount}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                Not Uploaded (Errors)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            sx={{
              backgroundColor: 'rgba(245, 248, 252, 1) !important',
              border: '2px solid #FF9800',
              borderRadius: 2,
              height: '100%',
              boxShadow: '0 2px 8px rgba(255, 152, 0, 0.15)',
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <SkipNextIcon sx={{ fontSize: 48, color: '#FF9800', mb: 1 }} />
              <Typography variant="h4" sx={{ color: '#FF9800', fontWeight: 700 }}>
                {summary.skippedCount}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                Skipped (Deselected)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* {(summary.errorCount > 0 || summary.skippedCount > 0) && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 1 }}>
          <Typography variant="body2">
            <strong>Summary:</strong> {summary.successCount} GDO(s) uploaded successfully.
            {summary.errorCount > 0 && ` ${summary.errorCount} row(s) failed validation.`}
            {summary.skippedCount > 0 && ` ${summary.skippedCount} valid row(s) were skipped.`}
          </Typography>
        </Alert>
      )} */}

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={onDownloadReport}
          size="large"
          sx={{
            minWidth: 220,
            fontWeight: 600,
            borderColor: '#009BD8',
            color: '#009BD8',
            '&:hover': { borderColor: '#007BB8', backgroundColor: 'rgba(0, 155, 216, 0.04)' },
          }}
        >
          Download Status Report
        </Button>

        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={onDownloadInvalidTemplate}
          size="large"
          sx={{
            minWidth: 260,
            fontWeight: 600,
            borderColor: '#FF9800',
            color: '#FF9800',
            '&:hover': { borderColor: '#E68900', backgroundColor: 'rgba(255, 152, 0, 0.04)' },
          }}
        >
          Download Invalid Entries Template
        </Button>

        <Button
          variant="outlined"
          onClick={onGoToGDOList}
          size="large"
          sx={{
            minWidth: 180,
            fontWeight: 600,
            borderColor: '#71758F',
            color: '#71758F',
          }}
        >
          Go to GDO List
        </Button>

        <Button
          variant="contained"
          onClick={onUploadAnother}
          size="large"
          sx={{
            minWidth: 200,
            fontWeight: 600,
            backgroundColor: '#003771',
            color: 'white',
            '&:hover': { backgroundColor: '#002B5C' },
          }}
        >
          Upload Another File
        </Button>
      </Box>
    </Box>
  );
}

export default GDOStatusSummary;